import { Star, ArrowUpRight } from "lucide-react";

const REVIEW_URL = "https://www.google.com/search?q=waites+clearances+peterborough";

function Stars({ size = 16, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="text-[#F5B400]"
          fill="#F5B400"
          strokeWidth={0}
          style={{ width: size, height: size }}
        />
      ))}
    </span>
  );
}

// GoogleG mark – lightweight inline SVG (no external CDN)
function GoogleG({ className = "" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export default function GoogleReviewsBadge({ variant = "card", className = "" }) {
  if (variant === "compact") {
    return (
      <a
        href={REVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="google-reviews-compact"
        className={`inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#FF7F00] hover:text-[#FF7F00] transition-colors ${className}`}
      >
        <GoogleG className="h-4 w-4" />
        <Stars size={12} />
        <span>5.0 · 10 reviews</span>
        <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
    );
  }

  if (variant === "inline") {
    return (
      <a
        href={REVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="google-reviews-inline"
        className={`inline-flex items-center gap-2.5 rounded-full bg-white/95 backdrop-blur px-4 py-2 shadow-lg shadow-black/20 text-slate-900 text-sm font-semibold hover:bg-white transition-colors ${className}`}
      >
        <GoogleG className="h-4 w-4" />
        <Stars size={14} />
        <span className="text-slate-900">5.0</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-600">10 Google reviews</span>
        <ArrowUpRight className="h-4 w-4 text-[#FF7F00]" />
      </a>
    );
  }

  // "card" (default) – prominent trust badge for homepage strip
  return (
    <section
      data-testid="google-reviews-card"
      className={`py-14 sm:py-16 bg-white border-b border-slate-100 ${className}`}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10">
        <div className="relative overflow-hidden rounded-3xl border border-[#FFB870] bg-gradient-to-br from-[#FFF7EE] via-white to-white p-7 sm:p-10">
          <div className="absolute -right-24 -top-24 h-56 w-56 rounded-full bg-[#FF7F00]/10 blur-3xl" />

          <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex items-center gap-4 shrink-0">
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                <GoogleG className="h-8 w-8 sm:h-9 sm:w-9" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B85800]">
                  Rated on Google
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 leading-none">
                    5.0
                  </span>
                  <Stars size={22} />
                </div>
                <p className="mt-1.5 text-sm text-slate-600">
                  10 reviews from local customers
                </p>
              </div>
            </div>

            <div className="hidden md:block h-16 w-px bg-slate-200" />

            <div className="flex-1">
              <p className="font-display text-lg sm:text-xl font-bold text-slate-900 leading-snug">
                A family run team, trusted by locals in Peterborough.
              </p>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Read what our customers say and leave a review of your own on Google.
              </p>
            </div>

            <a
              href={REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-cta"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 transition-colors shrink-0"
            >
              Read our Google reviews
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
